import { useContext } from 'react';
import logTrailCardAddToListButtonTapped from '@alltrails/analytics/events/logTrailCardAddToListButtonTapped';
import NewTrailCard from '@alltrails/shared/components/TrailCard';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { Context } from '@alltrails/shared/types/context';
import { Trail, TrailId } from 'types/Trails';
import useActivityDuration from 'hooks/useActivityDuration';
import { getTrailForTrailCard } from 'utils/cardInfo';
import { logTrailCardClickedFromPage } from 'utils/trail_analytics_helpers';
import { PageContext } from 'components/shared/Page/Page';

type TrailCardProps = {
  variant?: 'standard' | 'wide';
  trail: Trail;
  context: Context;
  handleCardClick?: () => void;
  handleFavoriteClick: () => void;
  isFavorite?: boolean;
  isCompleted?: boolean;
  isVerified?: boolean;
  messagingChannel?: {
    publish: (event: string, track: Trail) => void;
  };
  allowItemMoving?: boolean;
  dragHandleProps?: { [key: string]: any };
  allowItemDelete?: boolean;
  handleItemDeleteClick?: () => void;
  itemDeleteLabel?: string;
  displayRank?: number;
  className?: string;
  trailUrl?: string;
  maxPhotos?: number;
  handleClickAnalytics?: () => void;
  allowClickingCompletedBadge?: boolean;
  handleCompletedBadgeClick?: () => void;
};

const TrailCard = ({
  variant = 'standard',
  trail,
  context,
  handleCardClick,
  handleFavoriteClick,
  isFavorite,
  isCompleted,
  isVerified,
  messagingChannel,
  allowItemMoving,
  dragHandleProps,
  allowItemDelete,
  handleItemDeleteClick,
  itemDeleteLabel,
  displayRank,
  className,
  trailUrl,
  maxPhotos,
  handleClickAnalytics,
  allowClickingCompletedBadge,
  handleCompletedBadgeClick
}: TrailCardProps): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();
  const minutes = useActivityDuration(trail);
  const {
    globalState: { pageSource }
  } = useContext(PageContext);

  const deleteButton =
    allowItemDelete && handleItemDeleteClick && itemDeleteLabel
      ? {
          title: itemDeleteLabel,
          onClick: handleItemDeleteClick
        }
      : undefined;

  const onCardClick = () => {
    logTrailCardClickedFromPage(trail.ID as TrailId, pageSource);

    if (handleClickAnalytics) {
      handleClickAnalytics();
    }
    if (handleCardClick) {
      handleCardClick();
    }
  };

  const onFavoriteClick = () => {
    logTrailCardAddToListButtonTapped({ trail_id: trail.ID });
    handleFavoriteClick();
  };

  const card = (
    <NewTrailCard
      variant={variant}
      languageRegionCode={languageRegionCode}
      onCardClick={onCardClick}
      onCardMouseEnter={() => messagingChannel?.publish?.('item.mouseenter', trail)}
      onCardMouseLeave={() => messagingChannel?.publish?.('item.mouseleave', trail)}
      onFavoriteClick={onFavoriteClick}
      isFavorite={isFavorite}
      isCompleted={isCompleted}
      isVerified={isVerified}
      trail={getTrailForTrailCard(trail, minutes, languageRegionCode)}
      url={trailUrl}
      blockNavigation={!!handleCardClick} // Navigation is handled by the click handler - this is existing behavior in TrailResultCard :(
      useMetric={context?.displayMetric}
      maxPhotos={maxPhotos}
      dragHandleProps={allowItemMoving ? dragHandleProps : undefined}
      deleteButton={deleteButton}
      displayRank={displayRank}
      onCompletionBadgeClick={allowClickingCompletedBadge ? handleCompletedBadgeClick : undefined}
    />
  );

  return className ? <div className={className}>{card}</div> : card;
};

export default TrailCard;
